package guide.kuki.app.engine

import kotlinx.serialization.json.Json

private const val NAMES_URL = "/files/names.json"
private const val SECTIONS_URL = "/files/map_sections.json"
private const val RADIALS_URL = "/files/map_radials.json"

/**
 * Loads and caches the [RadialMap] from the JSON assets served alongside
 * index.html.
 */
class RadialMapProvider {
    private var cached: RadialMap? = null
    private var names: Map<String, String> = emptyMap()

    private val json = Json {
        ignoreUnknownKeys = true
        allowTrailingComma = true
    }

    suspend fun loadNames() {
        val bytes = fetchRemoteNamesBytes() ?: platformFetchBytes(NAMES_URL) ?: return
        val text = bytes.decodeToString()
        // A malformed names.json must not leave the app non-functional —
        // fall back to engine-generated defaults (Atai, Etai, …) instead.
        names = try {
            val entries: Map<String, NameEntry> = json.decodeFromString(text)
            entries.toAddressNames()
        } catch (_: Throwable) {
            emptyMap()
        }
    }

    suspend fun get(): RadialMap {
        cached?.let { return it }

        val sectionsBytes = platformFetchBytes(SECTIONS_URL)
            ?: error("Failed to load $SECTIONS_URL")
        val radialsBytes = platformFetchBytes(RADIALS_URL)
            ?: error("Failed to load $RADIALS_URL")

        val sectionsData: MapSectionsData =
            json.decodeFromString(sectionsBytes.decodeToString())
        val radialsData: MapRadialsData =
            json.decodeFromString(radialsBytes.decodeToString())

        val map = buildRadialMap(radialsData.radials, sectionsData.sections)
        if (names.isNotEmpty()) {
            map.loadAddresses(names)
        }
        cached = map
        return map
    }
}
